import { Pause, Play, Phone, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from './StatusBadge';
import type { Conversa } from '@/types';

interface ConversaCardProps {
  conversa: Conversa;
  isSelected: boolean;
  onSelect: () => void;
  onToggleIA: () => void;
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `há ${diffMins}min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays < 7) return `há ${diffDays}d`;
  return date.toLocaleDateString('pt-BR');
}

function formatPhone(numero: string | null): string {
  if (!numero) return 'Número não identificado';
  const cleaned = numero.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return numero;
}

export function ConversaCard({ conversa, isSelected, onSelect, onToggleIA }: ConversaCardProps) {
  const displayName = conversa.lead_nome || conversa.push_name || formatPhone(conversa.numero_real || conversa.channel_id) || 'Número desconhecido';
  const isDisabled = conversa.is_ai_disabled;

  return (
    <div
      onClick={onSelect}
      className={`
        relative p-4 rounded-xl border cursor-pointer transition-all duration-200
        ${isSelected
          ? 'border-ds-success/50 bg-ds-success/[0.03] shadow-lg'
          : 'border-ds-border bg-ds-surface hover:border-ds-success/30 hover:shadow-md'
        }
        ${isDisabled ? 'border-l-4 border-l-ds-danger' : 'border-l-4 border-l-ds-success'}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isDisabled ? 'bg-ds-danger/10' : 'bg-ds-success/10'
          }`}>
            <span className={`text-sm font-medium ${isDisabled ? 'text-ds-danger' : 'text-ds-success'}`}>
              {displayName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-ds-text-primary truncate">{displayName}</p>
            <div className="flex items-center gap-2 text-xs text-ds-text-secondary mt-0.5">
              <Phone className="h-3 w-3" />
              <span>{formatPhone(conversa.numero_real || conversa.channel_id)}</span>
            </div>
          </div>
        </div>

        <StatusBadge
          is_ai_disabled={conversa.is_ai_disabled}
          msg_count_24h={conversa.msg_count_24h}
          teve_reativacao_recente={conversa.teve_reativacao_recente}
        />
      </div>

      {conversa.ultima_mensagem && (
        <div className="mt-3 flex items-start gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-ds-text-secondary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-ds-text-secondary line-clamp-1 flex-1">
            {conversa.ultima_mensagem}
          </p>
          <span className="text-xs text-ds-text-secondary flex-shrink-0">
            {formatTimeAgo(conversa.ultima_mensagem_at)}
          </span>
        </div>
      )}

      <div className="mt-3 flex items-center justify-end">
        <Button
          variant={isDisabled ? 'primary' : 'danger'}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onToggleIA();
          }}
        >
          {isDisabled ? (
            <>
              <Play className="h-3.5 w-3.5 mr-1.5" />
              Ligar IA
            </>
          ) : (
            <>
              <Pause className="h-3.5 w-3.5 mr-1.5" />
              Desligar IA
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
